import { auth } from "src/firebase";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserController } from "src/components/user";
import { signInWithCustomToken } from "firebase/auth";
import { ReactNode, useEffect, useState } from "react";
import { ErrorHandler } from "src/abstracts/handleError";
import { UpdatePasswordScreenForm } from "./sections/form";
import { UpdatePasswordScreenReseted } from "./sections/reseted";
import { UpdatePasswordScreenMissingToken } from "./sections/missing-token";
import { FirebaseAuthErrorHandler } from "src/utils/FirebaseAuthErrorHandler";

enum UpdatePasswordScreenSectionKey {
  form = "form",
  reseted = "reseted",
  missingToken = "missingToken",
}

export default function UpdatePasswordScreen() {
  const { t } = useTranslation();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token: string | null =
    typeof queryParams.get("token") === "string"
      ? queryParams.get("token")
      : null;

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [sectionKey, setSectionKey] = useState<UpdatePasswordScreenSectionKey>(
    UpdatePasswordScreenSectionKey.form,
  );

  const onEmailChanged = (email: string) => {
    setEmail(email);
  };

  const onPasswordChanged = (password: string) => {
    setPassword(password);
  };

  const onConfirmPasswordChanged = (confirmPassword: string) => {
    setConfirmPassword(confirmPassword);
  };

  const onResetPasswordButtonClicked = async () => {
    // Make sure token exists
    if (token === null) {
      setSectionKey(UpdatePasswordScreenSectionKey.missingToken);
      return;
    }

    try {
      const { authToken } = await UserController.updatePassword({
        idToken: token,
        newPassword: password,
      });
      // Sign in using authToken
      try {
        await signInWithCustomToken(auth, authToken);
      } catch (e) {
        FirebaseAuthErrorHandler.handle(e, t);
        return;
      }
      // Set section to reseted
      setSectionKey(UpdatePasswordScreenSectionKey.reseted);
    } catch (e) {
      ErrorHandler.displayError(e);
    }
  };

  useEffect(() => {
    if (token === null)
      setSectionKey(UpdatePasswordScreenSectionKey.missingToken);
  }, [token]);

  const buildSectionFromSectionKey = (
    sectionKey: UpdatePasswordScreenSectionKey,
  ): ReactNode => {
    switch (sectionKey) {
      case UpdatePasswordScreenSectionKey.form:
        return (
          <UpdatePasswordScreenForm
            email={email}
            password={password}
            confirmPassword={confirmPassword}
            onEmailChanged={onEmailChanged}
            onPasswordChanged={onPasswordChanged}
            onConfirmPasswordChanged={onConfirmPasswordChanged}
            onResetPasswordButtonClicked={onResetPasswordButtonClicked}
          />
        );
      case UpdatePasswordScreenSectionKey.reseted:
        return <UpdatePasswordScreenReseted />;
      case UpdatePasswordScreenSectionKey.missingToken:
        return <UpdatePasswordScreenMissingToken />;
    }
  };

  return (
    <main className="flex flex-col desktop:flex-row w-full tablet:min-h-[100svh] desktop:h-[100svh] items-center justify-center overflow-hidden">
      {buildSectionFromSectionKey(sectionKey)}
    </main>
  );
}
